// The day's WhatsApp onayları in one go (Onur, 22.10.2026): every child
// on the shown day, grouped by danışman, a tick each, Tümü / Hiçbiri,
// one send. A child can be ticked only when the drawer's own button
// would send: the seans is on and not started, the client is in the
// pilot, and nothing was asked yet (or the last send failed). The rest
// stay listed with the reason, so the desk sees the whole day: as the
// session box's own icon where the grid has one for it (Onur,
// 31.10.2026), else as a word.

import {useMemo, useState} from "react";
import type {BoardCell, BoardClient, DayBoard} from "../api/contract";
import {Sheet} from "../actions/Sheets";
import {STATE_SIGN} from "../grid/signs";
import {hasStarted, waWord} from "../labels";
import {Avatar} from "../ui/Avatar";
import {Icon} from "../ui/Icon";

interface Row {
  key: string;
  cell: BoardCell;
  client: BoardClient;
  why: Why | null; // null = may be sent
}

// Why a child cannot be ticked. `icon` is how the session box on the
// grid shows the same thing: the round sign of Geliyor (WhatsApp and
// phone alike, one tick as on the grid) and of Gelmiyor, or the blue
// speech bubble of a written reply. The row then shows the icon and
// the word is its tooltip. İptal, seans başladı, pilot dışı,
// gönderiliyor and yanıt yok have no icon on the grid: they stay words.
interface Why {
  word: string;
  icon: "confirmed" | "declined" | "reply" | null;
}

function whyOf(cell: BoardCell, client: BoardClient, started: boolean): Why | null {
  const {state, source, eligible} = client.attendance;
  if (cell.status === "cancelled" || cell.status === "paused") return {word: "iptal", icon: null};
  if (started) return {word: "seans başladı", icon: null};
  if (!eligible) return {word: "pilot dışı", icon: null};
  if (state === "none" || state === "failed") return null;
  const word = state === "confirmed" && source === "phone" ? "telefonda onaylandı" :
    waWord(state, source)?.word ?? state;
  const icon = state === "confirmed" ? "confirmed" : state === "declined" ? "declined" :
    state === "text" ? "reply" : null;
  return {word, icon};
}

// The right of a row: the reason's icon, or its word.
function WhyMark({why}: {why: Why | null}) {
  if (why?.icon === "reply") {
    return (
      <span className="gc-mark blue" role="img" aria-label={why.word} title={why.word}>
        <Icon name="msg" />
      </span>
    );
  }
  if (why?.icon) {
    const sign = STATE_SIGN[why.icon];
    return (
      <span className={`gc-sign st-${why.icon}`} role="img" aria-label={why.word} title={why.word}>
        {sign && <Icon name={sign} />}
      </span>
    );
  }
  return <span className="dm-v soft">{why?.word ?? ""}</span>;
}

const keyOf = (cell: BoardCell, client: BoardClient) => `${cell.id}|${client.clientId}`;

export function WaDaySheet({board, today, nowMin, busy, onSend, onClose}: {
  board: DayBoard;
  today: string;
  nowMin: number;
  busy: string | null;
  onSend: (items: {cell: BoardCell; client: BoardClient}[],
    onStep: (done: number) => void) => Promise<void>;
  onClose: () => void;
}) {
  const groups = useMemo(() => board.providers.map((p) => {
    const rows: Row[] = board.cells
      .filter((c) => c.providerId === p.providerId)
      .sort((a, b) => a.time.localeCompare(b.time))
      .flatMap((cell) => cell.clients.map((client): Row => ({
        key: keyOf(cell, client), cell, client,
        why: whyOf(cell, client, hasStarted(board.date, cell.time, today, nowMin)),
      })));
    return {provider: p, rows};
  }).filter((g) => g.rows.length > 0), [board, today, nowMin]);

  const open = groups.flatMap((g) => g.rows).filter((r) => r.why === null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(open.map((r) => r.key)));
  const [done, setDone] = useState<number | null>(null);
  const chosen = open.filter((r) => picked.has(r.key));
  const sending = busy === "wa:day";

  const toggle = (key: string) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const head = (
    <header className="sd-head">
      <div className="sd-who">
        <h2 className="sd-title">WhatsApp onayları</h2>
        <span className="spacer" />
        <button className="sd-round" aria-label="Kapat" onClick={onClose}><Icon name="x" /></button>
      </div>
      <p className="sd-sub">
        {board.date === today ? "Bugünün" : `${board.date.slice(8, 10)}.${board.date.slice(5, 7)}`}
        {" "}seansları · {open.length} danışana sorulabilir
      </p>
      {open.length > 0 && (
        <div className="sd-chips">
          <button className="sd-chip" disabled={sending}
            onClick={() => setPicked(new Set(open.map((r) => r.key)))}>Tümü</button>
          <button className="sd-chip" disabled={sending}
            onClick={() => setPicked(new Set())}>Hiçbiri</button>
        </div>
      )}
    </header>
  );

  return (
    <Sheet className="seans-sheet wa-day" head={head} onClose={onClose}>
      {groups.length === 0 && <p className="sd-quiet">Bu gün seans yok.</p>}
      {groups.map(({provider, rows}) => (
        <section key={provider.providerId} className="dm-card">
          <h3 className="dm-card-head">
            <span className="with-avatar">
              <Avatar providerId={provider.providerId} name={provider.name} photo={provider.photo} />
              {provider.name}
            </span>
          </h3>
          <ul className="dm-rows">
            {rows.map((r) => (
              <li key={r.key} className="dm-row">
                <label className={r.why ? "wd-pick off" : "wd-pick"}>
                  <input type="checkbox" disabled={r.why !== null || sending}
                    checked={r.why === null && picked.has(r.key)}
                    onChange={() => toggle(r.key)} />
                  <span className="pd-time">{r.cell.time}</span>
                  <span className="pd-n">{r.client.name}</span>
                </label>
                <WhyMark why={r.why} />
              </li>
            ))}
          </ul>
        </section>
      ))}
      <button className="sd-go" disabled={chosen.length === 0 || busy !== null}
        onClick={() => {
          setDone(0);
          void onSend(chosen.map(({cell, client}) => ({cell, client})), setDone)
            .then(onClose);
        }}>
        {sending ? `Gönderiliyor… ${done ?? 0}/${chosen.length}` :
          chosen.length > 0 ? `${chosen.length} onay mesajı gönder` : "Seçili danışan yok"}
      </button>
    </Sheet>
  );
}
